import { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Card } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dayjs from 'dayjs';
import SoftBadge from 'components/common/SoftBadge';
import Flex from 'components/common/Flex';
import CreateReminderModal from 'components/dashboards/CreateReminderModal';
import { useRemindersForEntity, useCompleteReminder } from 'hooks/useReminders';

/**
 * Reminders linked to one donor or beneficiary, shown on their Notes tab (#28).
 * "Add reminder" pre-links the new reminder to this record.
 */
const LinkedReminders = ({ entityType, entityId, entityLabel }) => {
  const { reminders, isLoading } = useRemindersForEntity(entityType, entityId);
  const { completeReminder, isCompleting } = useCompleteReminder();
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);

  const pending = reminders.filter(r => r.status === 'pending');
  const done = reminders.filter(r => r.status !== 'pending');

  return (
    <Card className="mb-3 shadow-none border">
      <Card.Header className="py-2">
        <Flex
          justifyContent="between"
          alignItems="center"
          className="flex-wrap gap-2"
        >
          <h6 className="mb-0">
            <FontAwesomeIcon icon="bell" className="me-2 text-600" />
            Reminders{' '}
            {pending.length > 0 && (
              <SoftBadge pill bg="primary" className="ms-1">
                {pending.length}
              </SoftBadge>
            )}
          </h6>
          <Button
            size="sm"
            variant="falcon-primary"
            onClick={() => {
              setEditing(null);
              setShowModal(true);
            }}
          >
            <FontAwesomeIcon icon="plus" className="me-1" /> Add reminder
          </Button>
        </Flex>
      </Card.Header>
      <Card.Body className="py-2">
        {isLoading ? (
          <span className="text-600 fs--1">Loading…</span>
        ) : pending.length === 0 ? (
          <span className="text-600 fs--1">
            No open reminders{done.length ? ` · ${done.length} completed` : ''}
          </span>
        ) : (
          <ul className="list-unstyled mb-0">
            {pending.map(r => {
              const overdue = dayjs(r.due_date).isBefore(dayjs(), 'day');
              return (
                <li
                  key={r.id}
                  className="d-flex justify-content-between align-items-start gap-2 py-2 border-bottom"
                >
                  <div className="flex-1">
                    <div className="fw-semi-bold">{r.title}</div>
                    <div
                      className={`fs--1 ${
                        overdue ? 'text-danger' : 'text-600'
                      }`}
                    >
                      Due {dayjs(r.due_date).format('DD MMM YYYY')}
                      {r.due_time && ` ${String(r.due_time).slice(0, 5)}`}
                      {r.assigned_to &&
                        ` · ${r.assigned_to.first_name} ${r.assigned_to.last_name}`}
                      {r.repeat_rule &&
                        r.repeat_rule !== 'none' &&
                        ` · repeats ${r.repeat_rule}`}
                    </div>
                  </div>
                  <div className="d-flex gap-1 flex-shrink-0">
                    <Button
                      size="sm"
                      variant="falcon-default"
                      className="px-2 py-1"
                      title="Edit"
                      onClick={() => {
                        setEditing(r);
                        setShowModal(true);
                      }}
                    >
                      <FontAwesomeIcon icon="edit" className="fs--1" />
                    </Button>
                    <Button
                      size="sm"
                      variant="falcon-success"
                      className="px-2 py-1"
                      title="Complete"
                      disabled={isCompleting}
                      onClick={() => completeReminder(r.id)}
                    >
                      <FontAwesomeIcon icon="check" className="fs--1" />
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Card.Body>
      <CreateReminderModal
        show={showModal}
        onHide={() => setShowModal(false)}
        editingReminder={editing}
        defaultValues={{
          entity_type: entityType,
          entity_id: entityId,
          entity_label: entityLabel,
          reminder_type: 'follow_up'
        }}
      />
    </Card>
  );
};

LinkedReminders.propTypes = {
  entityType: PropTypes.oneOf(['donor', 'beneficiary']).isRequired,
  entityId: PropTypes.string,
  entityLabel: PropTypes.string
};

export default LinkedReminders;
